import React, { useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import Button from "components/lib/Shared/Button";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import AssignPermissionsSelection from "components/lib/Shared/CompanyDashboard/AssignPermission/Selection";
import { CORPORATES } from "components/PLCTABS/SFLdata/config/constants";
import {
  createPermission,
  editPermission,
  getPermissions,
  resetPermissionSlice,
} from "state/slices/permission";

import {
  createRole,
  editRole,
  getRoles,
  resetRoleSlice,
} from "state/slices/role";
import _omit from "lodash/omit";
import { useToast } from "hooks/useToast";
import { getEmployeeAsync, removeEmployeeAsync } from "state/slices/employee";

export default function SubRowAsync({ visibleColumns, row, employee = false }) {
  const closeSubRowRef = useRef(null);
  const { company } = useSelector((state) => state.companyDashboardSlice);

  const { permissions, getPermissionsStatus } = useSelector(
    (state) => state.permissionSlice
  );

  const { roles, getRolesStatus } = useSelector((state) => state.roleSlice);

  const { toast } = useToast(5000);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(resetPermissionSlice());
    dispatch(resetRoleSlice());
    dispatch(
      getPermissions({
        ...(employee
          ? { employee: row.original._id }
          : {
              [CORPORATES.includes(row.original.idType)
                ? "corporate"
                : "individual"]: row.original._id,
            }),
        company: company._id,
        status: "Confirmed",
      })
    );

    dispatch(
      getRoles({
        ...(employee
          ? { employee: row.original._id }
          : {
              [CORPORATES.includes(row.original.idType)
                ? "corporate"
                : "individual"]: row.original._id,
            }),
        company: company._id,
        status: "Confirmed",
      })
    );
  }, []);

  if (
    getPermissionsStatus.status === "loading" ||
    getRolesStatus.status === "loading"
  ) {
    return (
      <tr className="bg-[#EDF0F5]">
        <td colSpan={visibleColumns.length} className="px-9">
          <div className="flex justify-center mx-auto my-2 ">
            <SpinnerIcon className="text-gray-400" />
          </div>
        </td>
      </tr>
    );
  }

  let grantedPermission = permissions?.[0] ?? {};
  let grantedRole = roles?.[0] ?? {};

  let defaultAccessTypeSchema = {
    startDate: null,
    endDate: null,
    revokedAt: null,
    duration: "indefinite",
  };

  if (grantedPermission?.accessType) {
    const { accessType, startDate, endDate, revokedAt, ...rest } =
      grantedPermission;
    defaultAccessTypeSchema.duration = accessType ?? "indefinite";
    defaultAccessTypeSchema.startDate = startDate ?? null;
    defaultAccessTypeSchema.endDate = endDate ?? null;
    defaultAccessTypeSchema.revokedAt = revokedAt ?? null;
    grantedPermission = {
      ...rest,
    };
  }

  const handleAccess = async (values) => {
    const payload = _omit(values, ["_id", "createdAt", "updatedAt"]);
    const { dashboardPermissions, dashboardRoles, ...restValues } = payload;
    const toastMessage =
      grantedPermission?._id || grantedRole._id
        ? "Updated access successfully."
        : "Allowed access successfully.";
    let isSuccess = true;
    if (grantedPermission?._id) {
      try {
        await dispatch(
          editPermission({
            permissionId: grantedPermission._id,
            data: {
              dashboardPermissions,
              ...restValues,
            },
          })
        );
      } catch (error) {
        isSuccess = false;
        toast(
          "error",
          error?.message ?? "Something went wrong please try again."
        );
      }
    } else {
      try {
        await dispatch(
          createPermission({
            data: {
              dashboardPermissions,
              ...restValues,
              [CORPORATES.includes(row.original.idType)
                ? "corporate"
                : "individual"]: row.original._id,
              company: company._id,
              status: "Confirmed",
            },
          })
        );
      } catch (error) {
        isSuccess = false;
        toast(
          "error",
          error?.message ?? "Something went wrong please try again."
        );
      }
    }
    if (grantedRole?._id) {
      try {
        await dispatch(
          editRole({
            roleId: grantedRole._id,
            data: {
              dashboardRoles,
              ...restValues,
            },
          })
        );
      } catch (error) {
        isSuccess = false;
        toast(
          "error",
          error?.message ?? "Something went wrong please try again."
        );
      }
    } else {
      try {
        await dispatch(
          createRole({
            data: {
              dashboardRoles,
              ...restValues,
              [CORPORATES.includes(row.original.idType)
                ? "corporate"
                : "individual"]: row.original._id,
              company: company._id,
              status: "Confirmed",
            },
          })
        );
      } catch (error) {
        isSuccess = false;
        toast(
          "error",
          error?.message ?? "Something went wrong please try again."
        );
      }
    }

    if (isSuccess) {
      toast("success", toastMessage);
      closeSubRowRef.current.click();
    }
  };
  const handleDelete = async () => {
    try {
      await dispatch(removeEmployeeAsync({ employeeId: row.original._id }));
      await dispatch(getEmployeeAsync({ companyId: row.original.company }));
      toast("success", "Employee Deleted successfully.");
      closeSubRowRef.current.click();
    } catch (error) {
      toast(
        "error",
        error?.message ?? "Something went wrong please try again."
      );
    }
  };
  return (
    <tr className="bg-[#EDF0F5]">
      <td colSpan={visibleColumns.length} className="px-9">
        <div className="flex justify-between items-center mt-2">
          {/* button to close sub row programmatically */}
          {row.isExpanded && (
            <Button
              ref={closeSubRowRef}
              variant="text"
              className="space-x-0 gap-0"
              onClick={(e) => row.getToggleRowExpandedProps().onClick?.(e)}
              {...row.getToggleRowExpandedProps()}
            ></Button>
          )}
        </div>
        <p className="text-gray-500 text-sm mb-1">Access</p>
        <hr />
        <AssignPermissionsSelection
          initialValues={{
            accessType: {
              standardUser: defaultAccessTypeSchema,
              orgAdmin: defaultAccessTypeSchema,
              brsOps: defaultAccessTypeSchema,
              superAdmin: defaultAccessTypeSchema,
            },
            dashboardPermissions: [],
            dashboardRoles: [],
            ...grantedPermission,
            ...grantedRole,
          }}
          forEmployee={employee ? true : false}
          hasRoles={true}
          handleSubmit={(values) => {
            handleAccess(values);
            // if (grantedPermission?._id) handleUpdateAccess(values);
            // else handleGrantAccess(values);
          }}
          submitBtnLabel={
            grantedPermission?._id || grantedRole._id
              ? "Update Rights"
              : "Grant Rights"
          }
          loadingText={
            grantedPermission?._id || grantedRole._id
              ? "Updating..."
              : "Granting..."
          }
          handleDelete={handleDelete}
        />
      </td>
    </tr>
  );
}
